import React, { useState, useMemo } from "react";
import {
  KeyRound,
  Shield,
  Plus,
  CheckCircle2,
  Lock,
  Edit2,
  Trash2,
  Users,
  ShieldAlert,
  ShieldCheck,
  BookOpen,
  UserCheck,
  Layers,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Loader2,
  RefreshCw,
  FolderPlus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import {
  PermissionCategory,
  RoleGroupData,
  RoleSummary,
} from "../../services/userService";
import {
  useRbacMatrixQuery,
  useCreateCustomRoleMutation,
} from "../../hooks/queries/useUserAdminQueries";
import { useToast } from "../../hooks/otherHooks/useToast";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const SuperAdminRoles: React.FC = () => {
  const { addToast } = useToast();

  // Queries with cost-saving global caching (staleTime: 6 hours)
  const {
    data: matrixData,
    isLoading: isMatrixLoading,
    isFetching: isMatrixFetching,
    refetch: refetchMatrix,
  } = useRbacMatrixQuery();

  const roles = useMemo<RoleSummary[]>(
    () => (Array.isArray(matrixData?.roles) ? matrixData.roles : []),
    [matrixData?.roles]
  );
  const catalog = useMemo<PermissionCategory[]>(
    () => (Array.isArray(matrixData?.catalog) ? matrixData.catalog : []),
    [matrixData?.catalog]
  );
  const groups = useMemo<RoleGroupData[]>(
    () => (Array.isArray(matrixData?.groups) ? matrixData.groups : []),
    [matrixData?.groups]
  );

  const loading = isMatrixLoading && !matrixData;

  // Tabs: "tiers", "catalog", "groups"
  const [activeTab, setActiveTab] = useState<"tiers" | "catalog" | "groups">("tiers");

  // Create Custom Role Modal & Mutation
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupPermissions, setNewGroupPermissions] = useState<string[]>([]);

  const { mutateAsync: createCustomRoleMutation, isPending: isSubmittingGroup } =
    useCreateCustomRoleMutation();

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      addToast("Role group name is required.", "error");
      return;
    }

    try {
      const res = await createCustomRoleMutation({
        name: newGroupName.trim(),
        permissions: newGroupPermissions,
      });

      addToast(res.message || "Custom role group created!", "success");
      setNewGroupName("");
      setNewGroupPermissions([]);
      setIsCreateModalOpen(false);
    } catch (err: any) {
      console.error("Failed to create role group:", err);
      addToast(err?.response?.data?.detail || "Failed to create custom role group.", "error");
    }
  };

  const getRoleIcon = (roleId: string) => {
    switch (roleId) {
      case "SUPERADMIN":
        return <ShieldAlert size={22} className="cht-text-purple-600" />;
      case "ADMIN":
        return <ShieldCheck size={22} className="cht-text-slate-700" />;
      case "INSTRUCTOR":
        return <BookOpen size={22} className="cht-text-[#3e57da]" />;
      default:
        return <UserCheck size={22} className="cht-text-emerald-600" />;
    }
  };

  const getRoleBadgeColor = (roleId: string) => {
    switch (roleId) {
      case "SUPERADMIN":
        return "cht-bg-purple-100 cht-text-purple-800 cht-border-purple-200";
      case "ADMIN":
        return "cht-bg-slate-100 cht-text-slate-800 cht-border-slate-200";
      case "INSTRUCTOR":
        return "cht-bg-blue-50 cht-text-[#3e57da] cht-border-blue-200";
      default:
        return "cht-bg-emerald-50 cht-text-emerald-700 cht-border-emerald-200";
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-bg-[#3e57da]/10 cht-text-[#3e57da]">
              Security Architecture
            </span>
            <span className="cht-text-xs cht-text-gray-400 font-medium">Role-Based Access Control (RBAC)</span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
            Roles & Access Governance
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Platform role hierarchies, granular permission scopes, and custom authorization groups.
          </p>
        </div>
        <div className="cht-flex cht-items-center cht-gap-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw size={16} className={isMatrixFetching ? "cht-animate-spin" : ""} />}
            className="cht-rounded-2xl cht-border cht-border-gray-200"
            onClick={() => refetchMatrix()}
            disabled={isMatrixFetching}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus size={18} />}
            className="cht-rounded-2xl cht-px-6 cht-shadow-md hover:cht-shadow-lg"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Create Custom Role
          </Button>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="cht-flex cht-items-center cht-gap-2 cht-border-b cht-border-gray-200 cht-pb-2">
        <button
          onClick={() => setActiveTab("tiers")}
          className={`cht-px-5 cht-py-2.5 cht-rounded-2xl cht-text-xs cht-font-bold cht-transition-all ${
            activeTab === "tiers"
              ? "cht-bg-[#1e293b] cht-text-white cht-shadow-sm"
              : "cht-text-slate-500 hover:cht-text-slate-800 hover:cht-bg-gray-100"
          }`}
        >
          System Role Tiers ({roles.length})
        </button>

        <button
          onClick={() => setActiveTab("catalog")}
          className={`cht-px-5 cht-py-2.5 cht-rounded-2xl cht-text-xs cht-font-bold cht-transition-all ${
            activeTab === "catalog"
              ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
              : "cht-text-slate-500 hover:cht-text-slate-800 hover:cht-bg-gray-100"
          }`}
        >
          Permission Matrix Catalog ({catalog.reduce((acc, c) => acc + c.permissions.length, 0)})
        </button>

        <button
          onClick={() => setActiveTab("groups")}
          className={`cht-px-5 cht-py-2.5 cht-rounded-2xl cht-text-xs cht-font-bold cht-transition-all ${
            activeTab === "groups"
              ? "cht-bg-[#1e293b] cht-text-white cht-shadow-sm"
              : "cht-text-slate-500 hover:cht-text-slate-800 hover:cht-bg-gray-100"
          }`}
        >
          Custom Role Groups ({groups.length})
        </button>
      </div>

      {loading ? (
        <div className="cht-py-24 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3">
          <Loader2 size={32} className="cht-animate-spin cht-text-[#3e57da]" />
          <p className="cht-text-xs cht-font-bold cht-text-slate-500">Loading RBAC governance structure...</p>
        </div>
      ) : (
        <>
          {/* TAB 1: System Role Tiers */}
          {activeTab === "tiers" && (
            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-6">
              {roles.map((r) => (
                <GlassCard
                  key={r.id}
                  className="cht-p-7 cht-rounded-[32px] cht-space-y-6 cht-flex cht-flex-col cht-justify-between"
                >
                  <div className="cht-space-y-4">
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <div className="cht-flex cht-items-center cht-gap-3">
                        <div className="cht-p-3 cht-bg-slate-50 cht-rounded-2xl cht-border cht-border-slate-100">
                          {getRoleIcon(r.role)}
                        </div>
                        <div>
                          <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                            {r.name}
                          </h3>
                          <span className="cht-text-[10px] cht-font-mono cht-text-slate-400">
                            {r.id} • {r.role}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`cht-px-3 cht-py-1 cht-rounded-full cht-text-xs cht-font-black cht-border ${getRoleBadgeColor(
                          r.role
                        )}`}
                      >
                        {r.usersCount} Active Accounts
                      </span>
                    </div>

                    <p className="cht-text-xs cht-text-slate-600 leading-relaxed font-medium">
                      {r.description}
                    </p>
                  </div>

                  <div className="cht-space-y-3 cht-pt-4 cht-border-t cht-border-gray-100">
                    <p className="cht-text-[10px] cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider">
                      Authorizations & Capabilities
                    </p>
                    <div className="cht-flex cht-flex-wrap cht-gap-1.5">
                      {r.permissions.map((perm, i) => (
                        <span
                          key={i}
                          className="cht-px-2.5 cht-py-1 cht-bg-slate-100/80 cht-text-slate-700 cht-rounded-lg cht-text-[10px] cht-font-mono cht-font-bold"
                        >
                          {perm}
                        </span>
                      ))}
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}

          {/* TAB 2: Permission Matrix Catalog */}
          {activeTab === "catalog" && (
            <div className="cht-space-y-6">
              {catalog.map((cat) => (
                <GlassCard key={cat.category} className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
                  <div className="cht-border-b cht-border-gray-100 cht-pb-3">
                    <h3 className="cht-text-base cht-font-black cht-text-slate-900 !cht-font-heading">
                      {cat.category}
                    </h3>
                    <p className="cht-text-xs cht-text-slate-500 cht-mt-0.5">{cat.description}</p>
                  </div>

                  <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-3">
                    {cat.permissions.map((perm) => (
                      <div
                        key={perm.code}
                        className="cht-p-3.5 cht-rounded-2xl cht-bg-slate-50/70 cht-border cht-border-slate-100 cht-space-y-1.5"
                      >
                        <div className="cht-flex cht-items-center cht-justify-between">
                          <span className="cht-text-xs cht-font-bold cht-text-slate-900">
                            {perm.name}
                          </span>
                          <span className="cht-text-[9px] cht-font-mono cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-white cht-text-[#3e57da] cht-border cht-border-slate-200">
                            {perm.code}
                          </span>
                        </div>
                        <p className="cht-text-[11px] cht-text-slate-500 leading-snug">
                          {perm.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              ))}
            </div>
          )}

          {/* TAB 3: Custom Role Groups */}
          {activeTab === "groups" && (
            <div className="cht-space-y-6">
              {groups.length === 0 ? (
                <GlassCard className="cht-p-12 cht-rounded-[32px] cht-text-center cht-space-y-3">
                  <FolderPlus size={48} className="cht-text-slate-300 cht-mx-auto" />
                  <h4 className="cht-text-base cht-font-bold cht-text-slate-800">No Custom Groups Yet</h4>
                  <p className="cht-text-xs cht-text-slate-400 cht-max-w-md cht-mx-auto">
                    Create custom operational groups (e.g. Finance Officer, Deliverable Coordinator, Admissions Lead) and attach specific permissions.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="cht-rounded-xl cht-mt-2"
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    Create First Group
                  </Button>
                </GlassCard>
              ) : (
                <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-6">
                  {groups.map((group) => (
                    <GlassCard key={group.id} className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
                      <div className="cht-flex cht-items-center cht-justify-between">
                        <div>
                          <h4 className="cht-text-base cht-font-bold cht-text-slate-900">
                            {group.name}
                          </h4>
                          <span className="cht-text-[10px] cht-font-mono cht-text-slate-400">
                            Group ID #{group.id}
                          </span>
                        </div>
                        <span className="cht-px-3 cht-py-1 cht-rounded-full cht-text-xs cht-font-black cht-bg-slate-100 cht-text-slate-700">
                          {group.users_count} Member{group.users_count === 1 ? "" : "s"}
                        </span>
                      </div>

                      <div className="cht-pt-2 cht-border-t cht-border-gray-100">
                        <p className="cht-text-[10px] cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider cht-mb-2">
                          Attached Permissions ({group.permissions_list.length})
                        </p>
                        <div className="cht-flex cht-flex-wrap cht-gap-1.5">
                          {group.permissions_list.length > 0 ? (
                            group.permissions_list.map((code, i) => (
                              <span
                                key={i}
                                className="cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-slate-100 cht-text-slate-700 cht-text-[10px] cht-font-mono"
                              >
                                {code}
                              </span>
                            ))
                          ) : (
                            <span className="cht-text-xs cht-text-slate-400 font-medium">
                              No permissions attached yet
                            </span>
                          )}
                        </div>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Custom Role Group Modal */}
      {isCreateModalOpen && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4 cht-bg-slate-950/60 cht-backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="cht-bg-white cht-rounded-[28px] cht-max-w-xl cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden cht-flex cht-flex-col cht-max-h-[90vh]"
          >
            <div className="cht-p-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-shrink-0">
              <div className="cht-flex cht-items-center cht-gap-3">
                <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                    Create Custom Role Group
                  </h3>
                  <p className="cht-text-xs cht-text-slate-500 font-medium">
                    Define an operational group with tailored permissions
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="cht-w-8 cht-h-8 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateGroup} className="cht-p-6 cht-flex-1 cht-overflow-y-auto cht-space-y-5">
              <Input
                label="Role Group Name *"
                placeholder="e.g. Finance Officer, Admissions Lead"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                required
              />

              <div className="cht-space-y-3">
                <label className="cht-text-xs cht-font-black cht-text-slate-700 cht-uppercase cht-tracking-wider">
                  Select Permission Scopes
                </label>
                <div className="cht-space-y-4">
                  {catalog.map((cat) => (
                    <div key={cat.category} className="cht-p-3.5 cht-rounded-2xl cht-bg-slate-50 cht-border cht-border-slate-100 cht-space-y-2">
                      <h5 className="cht-text-xs cht-font-bold cht-text-slate-900">
                        {cat.category}
                      </h5>
                      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-2">
                        {cat.permissions.map((perm) => {
                          const isChecked = newGroupPermissions.includes(perm.code);
                          return (
                            <div
                              key={perm.code}
                              onClick={() => {
                                setNewGroupPermissions((prev) =>
                                  prev.includes(perm.code)
                                    ? prev.filter((p) => p !== perm.code)
                                    : [...prev, perm.code]
                                );
                              }}
                              className={`cht-p-2 cht-rounded-xl cht-border cht-cursor-pointer cht-flex cht-items-center cht-gap-2 cht-text-xs ${
                                isChecked ? "cht-bg-indigo-50/70 cht-border-[#3e57da] cht-text-[#3e57da] cht-font-bold" : "cht-bg-white cht-border-slate-200 cht-text-slate-700"
                              }`}
                            >
                              <div className={`cht-w-3.5 cht-h-3.5 cht-rounded cht-border cht-flex cht-items-center cht-justify-center ${
                                isChecked ? "cht-bg-[#3e57da] cht-border-[#3e57da] cht-text-white" : "cht-border-slate-300"
                              }`}>
                                {isChecked && <Check size={10} strokeWidth={3} />}
                              </div>
                              <span className="cht-truncate">{perm.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3 cht-border-t cht-border-gray-100">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cht-rounded-xl"
                  onClick={() => setIsCreateModalOpen(false)}
                  disabled={isSubmittingGroup}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  className="cht-rounded-xl cht-px-6"
                  isLoading={isSubmittingGroup}
                >
                  Create Group
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminRoles;
